/**
 * BotTimedOutMessage
 */
import React, { useEffect, useRef } from 'react';
import { BotMessage } from '..';
import { TENANTS, TIME_SECONDS_TRIGGER_TIMEOUT } from '../../constants';
import { getTenantName } from '../../helpers';
import { logEvent, getCorrelationId, getConversationId } from '../../logging';
import { logEvent as logAnalyticsEvent } from '../../analytics';
import { Activity } from '../../types';
import { InputMode } from '../../hooks';
import { AmpFlagSet } from '../../amplitude';
import { MessageRenderer } from '../../interfaces/messageRenderer';
import { craftCustomActivityMessage } from '../../messageUtils';
import quickMessageConfig from '../../resources/quickMessageConfig.json';
import { createMASendMessageButton } from '../../messageassist';
import general from '../../resources/general.json';

const buildTimeoutActivity = (tenant: string): Activity => {
    if (tenant === TENANTS.MESSAGE_ASSIST) {
        return craftCustomActivityMessage(
            quickMessageConfig.timeoutMessageMA,
            'timeout_message',
            [
                {
                    contentType:
                        'application/providence.messageAssistTimeoutMessage',
                    content: {
                        buttons: [
                            createMASendMessageButton(
                                '',
                                '',
                                undefined,
                                general.providence.continueWithMessage
                            ),
                        ],
                    },
                },
            ]
        );
    }
    return craftCustomActivityMessage(quickMessageConfig.timeoutMessage);
};

export type BotMessageDeps = {
    featureFlags: AmpFlagSet;
    sendMessage: (message: string) => void | undefined;
    sendPostBack: (message: string) => void | undefined;
    sendEvent: (name: string, value: any) => void | undefined;
    setInputMode: (inputMode: InputMode) => void;
    messageRenderer: MessageRenderer;
};

type BotTimedOutMessageProps = {
    botMessageDeps: BotMessageDeps;
};

export const BotTimedOutMessage: React.FC<BotTimedOutMessageProps> = ({
    botMessageDeps,
}) => {
    const tenantName = getTenantName();
    const timeoutRef = useRef<HTMLDivElement | null>(null);

    // Run one-time side-effects when the timeout message mounts
    useEffect(() => {
        // Report that timeout was displayed to Amplitude & AppInsights
        logAnalyticsEvent('Loading Delay', {
            delay_time: `${TIME_SECONDS_TRIGGER_TIMEOUT}s`,
        });
        logEvent({
            name: 'SetDidTimeout',
            properties: {
                conversation_id: getConversationId(),
                correlation_id: getCorrelationId(),
            },
        });
        // Scroll to the timeout message when it mounts
        timeoutRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const timeoutActivity: Activity = buildTimeoutActivity(tenantName);

    return (
        <div ref={timeoutRef}>
            <BotMessage
                activity={timeoutActivity}
                disableButtons={false}
                displayBotName={true}
                featureFlags={botMessageDeps.featureFlags}
                idx={1}
                sendMessage={botMessageDeps.sendMessage}
                sendPostBack={botMessageDeps.sendPostBack}
                sendEvent={botMessageDeps.sendEvent}
                setInputMode={botMessageDeps.setInputMode}
                messageRenderer={botMessageDeps.messageRenderer}
            ></BotMessage>
        </div>
    );
};
